import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { findInEnum, findValueInEnum, maybe } from "@saleor/misc";
import {
  OrderFilterKeys,
  OrderListFilterOpts,
  PaymentType
} from "@saleor/motherOrders/components/OrderListPage/filters";
import { MallFilterData_malls_edges_node } from "@saleor/motherOrders/types/MallFilterData";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";

import { IFilterElement } from "../../../components/Filter";
import {
  CourierFilter,
  DeliveryTypeEnum,
  OrderFilterInput,
  PaymentChannelCodeEnum,
  SmopOrderStatusEnum,
  StoreTypeEnum
} from "../../../types/globalTypes";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleEnumValueQueryParam,
  getMultipleTextFieldQueryParam as getMultiText,
  getMultipleValueQueryParam,
  getSingleEnumValueQueryParam,
  getSingleValueQueryParam
} from "../../../utils/filters";
import {
  OrderListUrlFilters,
  OrderListUrlFiltersEnum,
  OrderListUrlFiltersWithMultipleValuesEnum,
  OrderListUrlQueryParams
} from "../../urls";

export const ORDER_FILTERS_KEY = "orderFilters";

export function getFilterOpts(
  params: OrderListUrlFilters
): OrderListFilterOpts {
  return {
    created: {
      active: maybe(
        () =>
          [params.createdFrom, params.createdTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.createdTo, ""),
        min: maybe(() => params.createdFrom, "")
      }
    },
    customer: {
      active: !!maybe(() => params.customer),
      value: params.customer
    },
    deliveryType: {
      active: maybe(() => params.deliveryType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.deliveryType.map(status =>
              findValueInEnum(status, DeliveryTypeEnum)
            )
          ),
        []
      )
    },
    orderCourier: {
      active: maybe(() => params.orderCourier !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.orderCourier.map(status =>
              findValueInEnum(status, CourierFilter)
            )
          ),
        []
      )
    },
    paymentType: {
      active: maybe(() => params.payment !== undefined, false),
      value: maybe(() => findValueInEnum(params.payment, PaymentType))
    },
    riderName: {
      active:
        !!maybe(() => params.riderFirstName) ||
        !!maybe(() => params.riderLastName),
      value: {
        firstName: params.riderFirstName,
        lastName: params.riderLastName
      }
    },
    shipping: {
      active: maybe(
        () =>
          [params.shippingFrom, params.shippingTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.shippingTo, ""),
        min: maybe(() => params.shippingFrom, "")
      }
    },
    store: {
      active: !!maybe(() => params.store),
      value: params.store
    },
    verticalType: {
      active: maybe(() => params.verticalType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.verticalType.map(status =>
              findValueInEnum(status, StoreTypeEnum)
            )
          ),
        []
      )
    }
  };
}

export function getFilterOptsWithMall(
  params: OrderListUrlFilters,
  malls: {
    initial: MallFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): OrderListFilterOpts {
  return {
    created: {
      active: maybe(
        () =>
          [params.createdFrom, params.createdTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.createdTo, ""),
        min: maybe(() => params.createdFrom, "")
      }
    },
    customer: {
      active: !!maybe(() => params.customer),
      value: params.customer
    },
    deliveryType: {
      active: maybe(() => params.deliveryType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.deliveryType.map(status =>
              findValueInEnum(status, DeliveryTypeEnum)
            )
          ),
        []
      )
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: params?.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    orderCourier: {
      active: maybe(() => params.orderCourier !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.orderCourier.map(status =>
              findValueInEnum(status, CourierFilter)
            )
          ),
        []
      )
    },
    paymentType: {
      active: maybe(() => params.payment !== undefined, false),
      value: maybe(() => findValueInEnum(params.payment, PaymentType))
    },
    riderName: {
      active:
        !!maybe(() => params.riderFirstName) ||
        !!maybe(() => params.riderLastName),
      value: {
        firstName: params.riderFirstName,
        lastName: params.riderLastName
      }
    },
    shipping: {
      active: maybe(
        () =>
          [params.shippingFrom, params.shippingTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.shippingTo, ""),
        min: maybe(() => params.shippingFrom, "")
      }
    },
    store: {
      active: !!maybe(() => params.store),
      value: params.store
    },
    verticalType: {
      active: maybe(() => params.verticalType !== undefined, false),
      value: maybe(
        () =>
          dedupeFilter(
            params.verticalType.map(status =>
              findValueInEnum(status, StoreTypeEnum)
            )
          ),
        []
      )
    }
  };
}

export function getFilterVariables(
  params: OrderListUrlFilters,
  defaultDelTypes?: string[]
): OrderFilterInput {
  const courTypes = maybe(() =>
    params.orderCourier.map(status => findInEnum(status, CourierFilter))
  );
  const paymentMethod = params?.payment?.split(":");
  const paymentMethodType =
    paymentMethod !== undefined && paymentMethod.length > 0
      ? paymentMethod[0]
      : null;
  const channelCode =
    paymentMethod !== undefined && paymentMethod.length > 0
      ? maybe(() => [findInEnum(paymentMethod[1], PaymentChannelCodeEnum)])
      : null;
  let delTypes = maybe(() =>
    params.deliveryType.map(status => findInEnum(status, DeliveryTypeEnum))
  );
  if (defaultDelTypes && !delTypes) {
    delTypes = defaultDelTypes.map(status =>
      findInEnum(status, DeliveryTypeEnum)
    );
  }

  return {
    brandName: params.store,
    courierTypes: courTypes,
    created: getGteLteVariables({
      gte: params.createdFrom,
      lte: params.createdTo
    }),
    customer: params.customer,
    deliverySchedules: getGteLteVariables({
      gte: params.shippingFrom,
      lte: params.shippingTo
    }),
    deliveryTypes: delTypes,
    malls: params.mall,
    paymentChannelCode: channelCode,
    paymentMethod: paymentMethodType,
    riderName: {
      firstName: params.riderFirstName,
      lastName: params.riderLastName
    },
    search: params.query,
    smopStatus:
      params.status !== undefined
        ? [findValueInEnum(params.status, SmopOrderStatusEnum)]
        : null,
    verticalType: maybe(() =>
      params.verticalType.map(status => findInEnum(status, StoreTypeEnum))
    )
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<OrderFilterKeys>
): OrderListUrlFilters {
  const { name } = filter;
  switch (name) {
    case OrderFilterKeys.created:
      return getMinMaxQueryParam(
        filter,
        OrderListUrlFiltersEnum.createdFrom,
        OrderListUrlFiltersEnum.createdTo
      );

    case OrderFilterKeys.status:
      return getSingleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersEnum.status,
        SmopOrderStatusEnum
      );
    case OrderFilterKeys.customer:
      return getSingleValueQueryParam(filter, OrderListUrlFiltersEnum.customer);

    case OrderFilterKeys.shipping:
      return getMinMaxQueryParam(
        filter,
        OrderListUrlFiltersEnum.shippingFrom,
        OrderListUrlFiltersEnum.shippingTo
      );

    case OrderFilterKeys.deliveryType:
      return getMultipleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersWithMultipleValuesEnum.deliveryType,
        DeliveryTypeEnum
      );

    case OrderFilterKeys.verticalType:
      return getMultipleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersWithMultipleValuesEnum.verticalType,
        StoreTypeEnum
      );

    case OrderFilterKeys.store:
      return getSingleValueQueryParam(filter, OrderListUrlFiltersEnum.store);

    case OrderFilterKeys.paymentType:
      return getSingleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersEnum.payment,
        PaymentType
      );

    case OrderFilterKeys.riderName:
      // const temp_firstName = filter?.numTextfield.find(
      //   e => e.name === OrderListUrlFiltersEnum.riderFirstName
      // ).value;
      // const temp_lastName = filter?.numTextfield.find(
      //   e => e.name === OrderListUrlFiltersEnum.riderLastName
      // ).value;
      return {
        riderFirstName: filter.active
          ? getMultiText(filter, OrderListUrlFiltersEnum.riderLastName) &&
            getMultiText(filter, OrderListUrlFiltersEnum.riderFirstName) ===
              undefined
            ? ""
            : getMultiText(filter, OrderListUrlFiltersEnum.riderFirstName)
          : undefined,

        riderLastName: filter.active
          ? getMultiText(filter, OrderListUrlFiltersEnum.riderFirstName) &&
            getMultiText(filter, OrderListUrlFiltersEnum.riderLastName) ===
              undefined
            ? ""
            : getMultiText(filter, OrderListUrlFiltersEnum.riderLastName)
          : undefined
      };

    case OrderFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        OrderListUrlFiltersWithMultipleValuesEnum.mall
      );

    case OrderFilterKeys.orderCourier:
      return getMultipleEnumValueQueryParam(
        filter,
        OrderListUrlFiltersWithMultipleValuesEnum.orderCourier,
        CourierFilter
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<OrderListUrlFilters>(ORDER_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  OrderListUrlQueryParams,
  OrderListUrlFilters
>({
  ...OrderListUrlFiltersEnum,
  ...OrderListUrlFiltersWithMultipleValuesEnum
});
